/**
 * LoomSelection — Variant C unified section-picker (Spec 19 v2).
 *
 * The surface the writer lands on AFTER choosing a doc-type +
 * library in LoomFresh, but BEFORE the run starts. Sections are
 * grouped into colored gate packets (always on — Spec 23 §4 config-
 * driven), dependencies auto-resolve (parents + deps pulled in and
 * marked "dep"), a compact write-strategy toggle previews the
 * live-vs-batched runtime behaviour, and the footer lets the writer
 * optionally generate a plan first or start drafting directly.
 *
 * SRP: presentation + local interaction state only. All data flows
 * in via props; the ``onStart`` callback hands the resolved
 * selection back to the shell, which is responsible for converting
 * section ids to backend ``module_ids`` (or ``gates``) and calling
 * ``startRun`` with the optional ``plan_id`` when show-plan is on.
 *
 * Accessibility:
 *   - gate-packet header is a button (role default); ``aria-pressed``
 *     reflects the select-all tri-state (all / some / none)
 *   - every checkbox row is role="checkbox" with ``aria-checked``
 *     tri-state and ``aria-labelledby`` pointing at the title span
 *   - strategy toggle is role="radiogroup" with two role="radio"
 *     buttons; (i) tooltip is keyboard-accessible via title
 *
 * # DECISION: write-strategy is PREVIEW-ONLY in v1 — per Spec 23 §5
 * #   Q1 the toggle adjusts the shell's local prediction but the
 * #   backend still honours ``generation.insertion_mode`` from YAML.
 * # WHY: we're addin-only for this roadmap; an authoritative
 * #      override needs ``StartRunRequest`` to accept
 * #      ``insertion_mode_override``, which is a backend change.
 * #      Preview teaches the writer the option exists without a
 * #      backend dependency; the authoritative mode lands when
 * #      backend work un-freezes.
 * # DATE: 2026-04-22
 *
 * # DECISION: default checked = all top-level sections.
 * # WHY: the most common writer intent at this surface is "draft the
 * #      whole thing" — opting out one-or-two is easier than opting
 * #      in fifteen. The design prototype shows the same default.
 * #      Writers who want a narrow set uncheck top-level and
 * #      re-select.
 * # DATE: 2026-04-22
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useCallback, useMemo, useState } from "react";
import {
  type GateMeta,
  type SelectionNode,
  type WriteStrategy,
  descendantsOf,
  groupByGate,
  resolveSelection,
} from "../selection";
import type { LeafOption } from "../leafDocuments";
import { brandColors, fonts, gateColorAt, inkColors, statusColors, surfaceColors } from "../tokens";
import { useFlag } from "../../context/FeatureFlagsContext";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** What the shell receives when the writer commits the picker. The
 *  shell is responsible for mapping ``resolvedSectionIds`` to
 *  backend module_ids (via DocTypeConfig.modules) and calling
 *  createPlan / startRun accordingly. */
export interface LoomSelectionCommit {
  /** Section ids in tree order — union of checked + implied. */
  resolvedSectionIds: string[];
  /** Subset of ``resolvedSectionIds`` the writer explicitly ticked.
   *  Useful when the shell wants to present "you picked N, we added
   *  K deps" copy in a downstream surface. */
  explicitSectionIds: string[];
  /** Writer's write-strategy choice. Preview-only in v1 — the
   *  shell may still use this for post-run UX hints. */
  strategy: WriteStrategy;
  /** When true the writer wants a plan-review surface before the
   *  run starts; when false they've chosen to skip plan review and
   *  start drafting immediately. */
  showPlan: boolean;
}

export interface LoomSelectionProps {
  /** Flat tree from ``buildSelectionTree(docConfig)``. */
  tree: SelectionNode[];
  /** Gate metadata from ``buildGateMeta(docConfig)``. Used for
   *  packet headers; order must match ``config.gates`` so the
   *  palette index is stable. */
  gates: GateMeta[];
  /** Display code for the header — "CSR", "CER", "PSUR/PBRER". */
  docCode: string;
  /** Secondary header label — "ICH E3", "EU MDR", etc. */
  docStandard: string;
  /** Backend's ``generation.insertion_mode`` for this doctype.
   *  Seeds the toggle. Null / undefined → default "live". */
  initialStrategy?: WriteStrategy;
  /** Initial checked set — defaults to every top-level section. */
  initialChecked?: string[];
  /** Non-actionable section ids (already drafted / locked). */
  lockedSectionIds?: string[];
  /** Already-drafted section ids that remain SELECTABLE — ticking one
   *  re-drafts (overwrites) it. Informational: shown with a "drafted" chip +
   *  overwrite tooltip and defaulted OFF, but (unlike ``lockedSectionIds``)
   *  NOT excluded from toggling or the commit. */
  draftedSectionIds?: string[];
  /** The eCTD leaves this doctype offers, or `null` when it is filed as ONE
   *  document — CSR, PROTOCOL_DRUG, PSUR and most others. `null` is the
   *  load-bearing value: the leaf step renders for nobody else, so those
   *  doctypes see exactly the picker they saw before leaves existed. */
  leaves?: LeafOption[] | null;
  /** Which leaf this document IS, once chosen. */
  leafDocumentId?: string | null;
  /** Writer picked a leaf. The shell persists the identity and re-scopes the
   *  tree; this component holds no leaf state of its own. */
  onLeafSelect?: (leafDocumentId: string) => void;
  /** Writer clicked the primary footer button. */
  onStart: (commit: LoomSelectionCommit) => void;
  /** Writer clicked Cancel — shell routes back to LoomFresh. */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

/**
 * Decide the primary footer button copy. Pure so the grammar is
 * testable.
 *
 *   - selectedCount === 0                 → "Select sections"
 *   - showPlan && canGo                   → "Generate plan"
 *   - !showPlan && strategy==='batched'   → "Start batched run"
 *   - !showPlan && strategy==='live'      → "Start drafting"
 */
export function formatPrimaryLabel(selectedCount: number, showPlan: boolean, strategy: WriteStrategy): string {
  if (selectedCount === 0) return "Select sections";
  if (showPlan) return "Generate plan";
  return strategy === "batched" ? "Start batched run" : "Start drafting";
}

/**
 * Compute the counts line for the picker header — "3 selected · +2
 * deps · ~14m". Pure + testable. ``impliedCount === 0`` drops the
 * "+K deps" clause so the line isn't noisy when nothing is implied.
 */
export function formatCountsLine(checkedTopLevel: number, impliedCount: number, totalEstMin: number): string {
  const parts: string[] = [`${checkedTopLevel} selected`];
  if (impliedCount > 0) parts.push(`+${impliedCount} deps`);
  parts.push(`~${totalEstMin}m`);
  return parts.join(" · ");
}

/**
 * Build the commit payload for ``onStart`` from the picker's resolved
 * state. Pure — the primary button and the "skip plan" link share
 * this helper so the contract ("which ids go out, in what order, with
 * which strategy + plan flag") has one source of truth and one place
 * to test.
 *
 * Guarantees:
 *   - Tree order preserved in both resolvedSectionIds and
 *     explicitSectionIds (downstream grouping relies on it).
 *   - resolvedSectionIds = checked ∪ implied.
 *   - explicitSectionIds = checked only (subset of resolved).
 *   - strategy + withPlan threaded through verbatim.
 */
export function buildCommitPayload(
  tree: SelectionNode[],
  checkedSet: ReadonlySet<string>,
  impliedSet: ReadonlySet<string>,
  strategy: WriteStrategy,
  withPlan: boolean,
): LoomSelectionCommit {
  // Grouping headers (drafts:false) own no module and are never drafted;
  // they shouldn't reach the backend even if one somehow slipped into a
  // set. Belt-and-suspenders — headers are already kept out of
  // checked/implied by the picker.
  const resolvedSectionIds = tree
    .filter((n) => n.drafts !== false && (checkedSet.has(n.n) || impliedSet.has(n.n)))
    .map((n) => n.n);
  const explicitSectionIds = tree.filter((n) => n.drafts !== false && checkedSet.has(n.n)).map((n) => n.n);
  return {
    resolvedSectionIds,
    explicitSectionIds,
    strategy,
    showPlan: withPlan,
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    // Anchors the overwrite-confirm overlay to the picker rather than the page.
    position: "relative",
    background: surfaceColors.paperDim,
    fontFamily: fonts.body,
  },
  header: {
    padding: "12px 14px 10px",
    background: "#fff",
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "6px",
  },
  headerTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: inkColors.ink,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  headerMeta: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  headerControls: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  selectAllButton: {
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: "12px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: "#fff",
    color: inkColors.ink2,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  selectAllButtonActive: {
    border: `1px solid ${brandColors.primary}`,
    background: brandColors.primary,
    color: "#fff",
  },
  counts: {
    fontSize: "10.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
  },
  // -- Strategy toggle ------------------------------------------------------
  strategyRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    background: "#fff",
    borderBottom: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  strategyLabel: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    fontWeight: 600,
  },
  strategyPill: {
    display: "flex",
    padding: "2px",
    background: surfaceColors.paperDim,
    borderRadius: "12px",
    border: `1px solid ${surfaceColors.line}`,
  },
  strategyOption: {
    padding: "3px 10px",
    fontSize: "10.5px",
    fontWeight: 500,
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    background: "transparent",
    color: inkColors.ink3,
  },
  strategyOptionActive: {
    background: "#fff",
    color: inkColors.ink,
    fontWeight: 700,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
  },
  strategyOptionDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    pointerEvents: "none",
  },
  strategyInfo: {
    width: "16px",
    height: "16px",
    borderRadius: "8px",
    border: `1.5px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink3,
    fontSize: "10px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "help",
    background: "#fff",
  },
  // -- Document dropdown (multi-leaf doctypes only) -------------------------
  // Mirrors LoomFresh's library picker: grow to fill, shrink then wrap so the
  // native <select> stays usable at default Word task-pane width.
  docPicker: {
    flex: "1 1 180px",
    minWidth: "120px",
    fontSize: "12px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "3px",
    padding: "4px 6px",
    fontFamily: fonts.body,
    background: surfaceColors.paper,
    color: inkColors.ink,
  },
  // Placeholder shown in the body while a multi-leaf doctype still awaits a
  // document pick — stands in for the section list so the whole doctype's
  // sections never render as one draftable set.
  awaitingLeaf: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    textAlign: "center",
    fontSize: "11.5px",
    fontFamily: fonts.body,
    color: inkColors.ink3,
  },
  // -- Body + gate packets --------------------------------------------------
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: "8px 0",
  },
  packet: {
    position: "relative",
    margin: "6px 10px 12px",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  packetStripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
  },
  packetHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px 8px 16px",
    cursor: "pointer",
    border: "none",
    width: "100%",
    textAlign: "left",
  },
  packetCheckbox: {
    width: "14px",
    height: "14px",
    borderRadius: "3px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  packetLabel: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
  },
  packetCount: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  packetRows: {
    padding: "4px 0",
  },
  // -- Rows -----------------------------------------------------------------
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "5px 12px",
    cursor: "pointer",
    borderRadius: "3px",
    "&:hover": {
      background: surfaceColors.paperDim,
    },
  },
  rowNumber: {
    minWidth: "56px",
    flexShrink: 0,
    fontSize: "10px",
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    fontFamily: fonts.mono,
  },
  rowCheckbox: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rowCheckboxBox: {
    width: "14px",
    height: "14px",
    borderRadius: "3px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  rowTitle: {
    fontSize: "12px",
    fontFamily: fonts.body,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  depChip: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    padding: "1px 5px",
    borderRadius: "3px",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  rowEst: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    flexShrink: 0,
  },
  // -- Footer ---------------------------------------------------------------
  footer: {
    padding: "12px 14px",
    background: "#fff",
    borderTop: `1px solid ${surfaceColors.line}`,
    flexShrink: 0,
  },
  planToggleRow: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    marginBottom: "10px",
    cursor: "pointer",
    fontSize: "10.5px",
    color: inkColors.ink3,
  },
  planRecommended: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    padding: "1px 5px",
    background: surfaceColors.paperDim,
    borderRadius: "3px",
  },
  // Subtle "skip plan" affordance — shown on the right of the plan-
  // toggle row. Writer-authorised addition to the footer (the design
  // only specifies the checkbox). Intentionally muted so it doesn't
  // compete with the recommended plan path; click = untick +
  // submit in one action.
  planRowSpacer: {
    flex: 1,
  },
  skipPlanLink: {
    fontSize: "10.5px",
    fontFamily: fonts.body,
    color: inkColors.ink4,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 2px",
    textDecoration: "underline",
    textDecorationColor: surfaceColors.lineStrong,
    textUnderlineOffset: "2px",
    "&:hover": {
      color: brandColors.primary,
      textDecorationColor: brandColors.primary,
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.4,
    },
  },
  footerButtons: {
    display: "flex",
    gap: "8px",
  },
  cancelButton: {
    padding: "9px 14px",
    fontSize: "11.5px",
    fontWeight: 600,
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: "#fff",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
  },
  primaryButton: {
    flex: 1,
    padding: "9px 14px",
    fontSize: "11.5px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    borderRadius: "6px",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontFamily: fonts.body,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  primaryButtonEnabled: {
    background: brandColors.primary,
    boxShadow: `0 2px 6px ${brandColors.primary}33`,
  },
  primaryButtonDisabled: {
    background: surfaceColors.lineStrong,
    cursor: "not-allowed",
  },
  primaryKeyHint: {
    fontSize: "10px",
    opacity: 0.75,
    fontFamily: fonts.mono,
  },
  // -- Overwrite confirmation ----------------------------------------------
  confirmOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "18px",
    background: "rgba(15, 15, 20, 0.42)",
  },
  confirmCard: {
    width: "100%",
    maxWidth: "300px",
    background: "#fff",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
    boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
    padding: "16px",
  },
  confirmTitle: {
    fontSize: "12px",
    fontWeight: 700,
    color: inkColors.ink,
    marginBottom: "6px",
  },
  confirmBody: {
    fontSize: "11px",
    lineHeight: 1.45,
    color: inkColors.ink3,
    marginBottom: "14px",
  },
  confirmButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** SVG checkmark — extracted so both the gate-header and row
 *  checkboxes render an identical mark. */
const Checkmark: React.FC<{ color?: string }> = ({ color = "#fff" }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <path
      d="M2 5 L4.2 7 L8 3"
      stroke={color}
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Small padlock glyph shown in a locked row's checkbox — marks an
 *  already-drafted section as non-selectable. */
const LockIcon: React.FC<{ color?: string }> = ({ color = "#fff" }) => (
  <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
    <rect x="2" y="4.5" width="6" height="4.5" rx="0.8" fill={color} />
    <path
      d="M3.2 4.5 V3 a1.8 1.8 0 0 1 3.6 0 v1.5"
      stroke={color}
      strokeWidth="1.1"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

export const LoomSelection: React.FC<LoomSelectionProps> = ({
  tree,
  gates,
  docCode,
  docStandard,
  initialStrategy = "live",
  initialChecked,
  lockedSectionIds,
  draftedSectionIds,
  leaves = null,
  leafDocumentId = null,
  onLeafSelect,
  onStart,
  onCancel,
}) => {
  const styles = useStyles();
  const liveFillDisabled = !useFlag("disable.live-fill");
  const lockedSet = useMemo(() => new Set(lockedSectionIds ?? []), [lockedSectionIds]);
  // Already-drafted-but-overwritable rows. Selectable and included in the
  // commit when ticked, but defaulted OFF so "Draft more" never silently
  // overwrites prior work.
  const draftedSet = useMemo(() => new Set(draftedSectionIds ?? []), [draftedSectionIds]);
  // Section id → node, for O(1) lookups (drafts flag, header cascade).
  const byId = useMemo(() => new Map(tree.map((n) => [n.n, n])), [tree]);

  // Default check = EVERY node (top-level + subsections). Earlier
  // we only ticked top-level which made "Select all" a lie for
  // doctypes like CER where most draftable content lives in §6.1,
  // §6.1.1, etc. — writers clicked "Select all" and 30+ subsections
  // stayed unchecked. Drafting subsections requires explicit ticks
  // (the resolver auto-includes parents but not children), so the
  // honest default is "every section", which is the writer's most
  // common intent at this surface.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable default computed once on mount
  const defaultChecked = useMemo(
    () =>
      (initialChecked ?? tree.map((n) => n.n)).filter(
        // Grouping headers (drafts:false) never seed ``checked`` — their
        // state is derived tri-state from their instructed descendants.
        (id) => !lockedSet.has(id) && !draftedSet.has(id) && byId.get(id)?.drafts !== false,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [checked, setChecked] = useState<string[]>(defaultChecked);
  const [strategy, setStrategy] = useState<WriteStrategy>(liveFillDisabled ? "batched" : initialStrategy);
  const [showPlan, setShowPlan] = useState<boolean>(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  // True while the overwrite confirmation is up — Start was pressed with
  // already-drafted sections in the selection. Cancel returns to the picker.
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  // Derived state — the resolver is cheap but memoising keeps row
  // renders reference-stable across toggles that don't change the
  // underlying sets.
  const { checkedSet, impliedSet, totalEstMin } = useMemo(() => resolveSelection(tree, checked), [tree, checked]);
  const effectiveCheckedSet = useMemo(() => {
    const next = new Set<string>();
    for (const id of checkedSet) {
      if (!lockedSet.has(id)) next.add(id);
    }
    return next;
  }, [checkedSet, lockedSet]);
  const effectiveImpliedSet = useMemo(() => {
    const next = new Set<string>();
    for (const id of impliedSet) {
      if (!lockedSet.has(id)) next.add(id);
    }
    return next;
  }, [impliedSet, lockedSet]);

  // Gate → palette color; stable via index lookup (no name-switch).
  const gateColorByName = useMemo(() => {
    const out = new Map<string, string>();
    gates.forEach((g, i) => out.set(g.name, gateColorAt(i)));
    return out;
  }, [gates]);

  const gateLabelByName = useMemo(() => {
    const out = new Map<string, string>();
    for (const g of gates) out.set(g.name, g.displayName);
    return out;
  }, [gates]);

  const groups = useMemo(() => groupByGate(tree), [tree]);
  // Counts: ALL nodes (incl. subsections), not top-level only — so
  // "N selected" reflects the writer's actual scope, and the
  // tri-state "Select all / Clear all" predicate is honest at every
  // depth. ``topLevelNodes`` is preserved as a helper for callers
  // that still want a top-level view, but the picker doesn't use it.
  const checkedCount = effectiveCheckedSet.size;

  // -- Handlers ---------------------------------------------------------
  // Toggling a row cascades down to its subsections: ticking a parent
  // auto-ticks every section nested under it, unticking clears them
  // too. Leaf rows toggle just themselves.
  const toggleRow = useCallback(
    (id: string) => {
      if (lockedSet.has(id)) return;
      const isHeader = byId.get(id)?.drafts === false;
      // A header cascades to its descendants only (it never enters
      // ``checked`` itself); a draftable row toggles itself + descendants.
      const raw = isHeader ? descendantsOf(tree, id) : [id, ...descendantsOf(tree, id)];
      // ``family`` is the set of ids actually written to ``checked`` —
      // draftable + unlocked only, so nested headers never pollute it.
      const family = new Set(
        raw.filter((fid) => !lockedSet.has(fid) && byId.get(fid)?.drafts !== false),
      );
      if (family.size === 0) return; // header whose only children are locked — inert
      setChecked((prev) => {
        // For a header, "on" means every instructed child is already
        // ticked; toggling then clears them. For a row, its own id.
        const wasChecked = isHeader
          ? Array.from(family).every((fid) => prev.includes(fid))
          : prev.includes(id);
        return wasChecked
          ? prev.filter((x) => !family.has(x))
          : Array.from(new Set([...prev, ...family]));
      });
    },
    [tree, lockedSet, byId],
  );

  // Gate-header checkbox toggles EVERY node in the gate (parents +
  // subsections), so the tri-state matches what the rows show.
  // Earlier we filtered ``!n.indent`` here — that left the gate
  // header showing "all checked" even when subsections weren't.
  const toggleGate = useCallback(
    (gateName: string, wasAllChecked: boolean) => {
      const inGate = tree
        .filter((n) => n.gate === gateName && !lockedSet.has(n.n) && n.drafts !== false)
        .map((n) => n.n);
      setChecked((prev) =>
        wasAllChecked ? prev.filter((x) => !inGate.includes(x)) : Array.from(new Set([...prev, ...inGate])),
      );
    },
    [tree, lockedSet],
  );

  const selectAll = useCallback(
    () =>
      setChecked(
        tree
          .filter((n) => !lockedSet.has(n.n) && !draftedSet.has(n.n) && n.drafts !== false)
          .map((n) => n.n),
      ),
    [tree, lockedSet, draftedSet],
  );
  const selectNone = useCallback(() => setChecked([]), []);

  // Only draftable rows are "actionable" — headers derive their state and
  // must not inflate the denominator (else ``allOn`` is never reached).
  const actionableCount = useMemo(
    () => tree.filter((n) => !lockedSet.has(n.n) && n.drafts !== false).length,
    [tree, lockedSet],
  );
  const checkedActionableCount = useMemo(
    () => tree.reduce((n, node) => n + (!lockedSet.has(node.n) && effectiveCheckedSet.has(node.n) ? 1 : 0), 0),
    [tree, lockedSet, effectiveCheckedSet],
  );
  const allOn = actionableCount > 0 && checkedActionableCount >= actionableCount;
  const selectedCount = effectiveCheckedSet.size + effectiveImpliedSet.size;
  // Multi-leaf doctypes (CMC / ICH M4Q) offer a document dropdown; every other
  // doctype has ``leaves === null`` and behaves exactly as before. Until a
  // document is chosen there is nothing to scope, so the section area shows a
  // placeholder and Start is disabled.
  const hasLeaves = leaves !== null && leaves.length > 0;
  const awaitingLeaf = hasLeaves && leafDocumentId == null;
  const canGo = selectedCount > 0 && !awaitingLeaf;

  // Already-drafted sections the run would re-draft (overwrite): anything in the
  // selection — ticked or auto-implied — that is in ``draftedSet``. Non-empty
  // means Start must confirm before committing.
  const selectedDrafted = useMemo(() => {
    const out: string[] = [];
    for (const id of effectiveCheckedSet) if (draftedSet.has(id)) out.push(id);
    for (const id of effectiveImpliedSet) if (draftedSet.has(id)) out.push(id);
    return out;
  }, [effectiveCheckedSet, effectiveImpliedSet, draftedSet]);

  /** Commit the selection. Takes an explicit ``withPlan`` so the
   *  primary button and the "skip plan" link can share one code
   *  path without tripping over React's async state updates
   *  (calling setShowPlan(false) then handleStart() in the same
   *  event would read the pre-update value). The payload
   *  construction itself is a pure helper (``buildCommitPayload``)
   *  covered by unit tests. */
  const handleCommit = useCallback(
    (withPlan: boolean) => {
      if (!canGo) return;
      onStart(buildCommitPayload(tree, effectiveCheckedSet, effectiveImpliedSet, strategy, withPlan));
    },
    [canGo, tree, effectiveCheckedSet, effectiveImpliedSet, strategy, onStart],
  );

  // Primary button — respects the writer's checkbox choice. When the selection
  // includes already-drafted sections, stop and confirm the overwrite first;
  // the confirmation's own button commits.
  const handleStart = useCallback(() => {
    if (!canGo) return;
    if (selectedDrafted.length > 0) {
      setConfirmOverwrite(true);
      return;
    }
    handleCommit(showPlan);
  }, [canGo, selectedDrafted, handleCommit, showPlan]);

  // Confirmation cleared — commit with the current plan choice.
  const handleConfirmOverwrite = useCallback(() => {
    setConfirmOverwrite(false);
    handleCommit(showPlan);
  }, [handleCommit, showPlan]);

  // Subtle "skip plan" link — always commits with showPlan=false
  // + also updates the checkbox state so the UI reflects the
  // writer's final choice (useful if this surface re-mounts).
  const handleSkipPlan = useCallback(() => {
    if (!canGo) return;
    setShowPlan(false);
    if (selectedDrafted.length > 0) {
      setConfirmOverwrite(true);
      return;
    }
    handleCommit(false);
  }, [canGo, selectedDrafted, handleCommit]);

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  return (
    <div className={styles.root}>
      {/* Header — title + select-all + counts */}
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <span className={styles.headerTitle}>Pick what to draft</span>
          <span style={{ flex: 1 }} />
          <span className={styles.headerMeta}>
            {docCode} · {docStandard}
          </span>
        </div>
        {/* Select-all + counts describe the section list, so they wait until a
            document is chosen (multi-leaf doctypes only). */}
        {!awaitingLeaf && (
          <div className={styles.headerControls}>
            <button
              type="button"
              className={mergeClasses(styles.selectAllButton, allOn && styles.selectAllButtonActive)}
              onClick={allOn ? selectNone : selectAll}
              aria-pressed={allOn}
            >
              {allOn ? "Clear all" : "Select all"}
            </button>
            <span className={styles.counts} aria-live="polite">
              {formatCountsLine(checkedCount, effectiveImpliedSet.size, totalEstMin)}
            </span>
          </div>
        )}
      </div>

      {/* WHICH document. CTD Module 2 is filed as 15 separate eCTD documents
          and Module 3 as 65, so those doctypes pick a document before its
          sections. `leaves` is null for every single-document doctype, so this
          dropdown renders for nobody else — a native <select> (not Fluent's
          Dropdown, which is jsdom-flaky) to match LoomFresh's library picker.
          Picking one re-scopes the section list below via the shell. */}
      {hasLeaves && (
        <div className={styles.strategyRow}>
          <span className={styles.strategyLabel}>Document</span>
          <select
            className={styles.docPicker}
            aria-label="Which document"
            value={leafDocumentId ?? ""}
            onChange={(e) => onLeafSelect?.(e.target.value)}
          >
            <option value="" disabled>
              Select a document…
            </option>
            {leaves?.map((leaf) => (
              <option key={leaf.id} value={leaf.id}>
                {leaf.id} {leaf.title} — {leaf.moduleCount} section{leaf.moduleCount === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Until a document is chosen there is nothing meaningful to scope, so the
          strategy toggle and section body are replaced by a placeholder — the
          whole doctype's sections must never render as one draftable set. */}
      {awaitingLeaf ? (
        <div className={styles.awaitingLeaf}>Select a document to choose its sections.</div>
      ) : (
        <>
      {/* Strategy toggle — preview-only. */}
      <div className={styles.strategyRow} role="radiogroup" aria-label="Write strategy">
        <span className={styles.strategyLabel}>Write strategy</span>
        <div className={styles.strategyPill}>
          {(["live", "batched"] as WriteStrategy[]).map((s) => {
            const on = strategy === s;
            return (
              <label
                key={s}
                className={mergeClasses(
                  styles.strategyOption,
                  on && styles.strategyOptionActive,
                  s === "live" && liveFillDisabled ? styles.strategyOptionDisabled : undefined,
                )}
                title={s === "live" && liveFillDisabled ? "Feature coming soon" : undefined}
              >
                <input
                  type="radio"
                  name="write-strategy"
                  value={s}
                  checked={on}
                  disabled={s === "live" && liveFillDisabled}
                  onChange={() => setStrategy(s)}
                  style={{
                    position: "absolute",
                    opacity: 0,
                    width: 0,
                    height: 0,
                    margin: 0,
                  }}
                />
                {s === "live" ? "Live fill" : "Batched"}
              </label>
            );
          })}
        </div>
        <button
          type="button"
          className={styles.strategyInfo}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(true)}
          onBlur={() => setTooltipOpen(false)}
          title={
            "Live fill — each section lands in the template as it finishes. " +
            "Batched — a gate generates all its sections first, then python-docx " +
            "assembles the doc in one pass. Cleaner formatting, but no mid-gate view."
          }
          aria-label="Strategy explainer"
        >
          i
        </button>
        {/* tooltipOpen is intentionally read but the rich tooltip
            overlay is deferred — title= gives the accessible version
            for now; a floating-panel tooltip ports cleanly later. */}
        <span aria-hidden={!tooltipOpen} style={{ display: "none" }} />
      </div>

      {/* Body — gate packets with rows */}
      <div className={styles.body}>
        {groups.map((group, groupIndex) => {
          const gc = gateColorByName.get(group.gate) ?? brandColors.primary;
          const displayName = gateLabelByName.get(group.gate) ?? group.gate;
          // Count over every DRAFTABLE node in the gate (parents +
          // subsections, excluding grouping headers) so the tri-state
          // header matches the rows. Headers derive their own state and
          // are never in ``checkedSet`` — counting them would peg the
          // gate header at "some" forever.
          const draftableNodes = group.nodes.filter((n) => n.drafts !== false);
          const actionableNodes = draftableNodes.filter((n) => !lockedSet.has(n.n));
          const allGateChecked = actionableNodes.length > 0 && actionableNodes.every((n) => checkedSet.has(n.n));
          const someGateChecked =
            !allGateChecked && actionableNodes.some((n) => checkedSet.has(n.n) || effectiveImpliedSet.has(n.n));

          return (
            <div key={`${group.gate}:${group.nodes[0]?.n ?? groupIndex}`} className={styles.packet}>
              <div className={styles.packetStripe} style={{ background: gc }} />
              <button
                type="button"
                className={styles.packetHeader}
                style={{
                  background: `${gc}08`,
                  borderBottom: `1px solid ${gc}22`,
                }}
                onClick={() => {
                  if (actionableNodes.length === 0) return;
                  toggleGate(group.gate, allGateChecked);
                }}
                aria-pressed={allGateChecked}
                aria-label={`Toggle all ${displayName} sections`}
              >
                <span
                  className={styles.packetCheckbox}
                  style={{
                    border: `1.5px solid ${gc}`,
                    background: allGateChecked ? gc : someGateChecked ? `${gc}55` : "#fff",
                  }}
                >
                  {allGateChecked && <Checkmark />}
                  {someGateChecked && !allGateChecked && (
                    <span
                      style={{
                        width: "6px",
                        height: "1.5px",
                        background: "#fff",
                      }}
                    />
                  )}
                </span>
                <span className={styles.packetLabel} style={{ color: gc }}>
                  {displayName}
                </span>
                <span className={styles.packetCount}>
                  {draftableNodes.length} section
                  {draftableNodes.length === 1 ? "" : "s"}
                </span>
              </button>
              <div className={styles.packetRows}>
                {group.nodes.map((node) => {
                  const isHeader = node.drafts === false;
                  // Header tri-state derives from its instructed, actionable
                  // descendants only (mirrors the gate-packet header above).
                  let headerAll = false;
                  let headerSome = false;
                  if (isHeader) {
                    const kids = descendantsOf(tree, node.n)
                      .map((kid) => byId.get(kid))
                      .filter((k): k is SelectionNode => !!k && k.drafts !== false && !lockedSet.has(k.n));
                    headerAll = kids.length > 0 && kids.every((k) => checkedSet.has(k.n));
                    headerSome =
                      !headerAll && kids.some((k) => checkedSet.has(k.n) || effectiveImpliedSet.has(k.n));
                  }
                  return (
                    <PickerRow
                      key={node.n}
                      node={node}
                      checked={checkedSet.has(node.n)}
                      implied={effectiveImpliedSet.has(node.n)}
                      locked={lockedSet.has(node.n)}
                      drafted={draftedSet.has(node.n)}
                      isHeader={isHeader}
                      headerAll={headerAll}
                      headerSome={headerSome}
                      gateColor={gc}
                      onToggle={toggleRow}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
        </>
      )}

      {/* Footer — show-plan toggle + primary + cancel */}
      <div className={styles.footer}>
        {/* Show-plan toggle row — commented out for now, keep for potential reuse
        <div className={styles.planToggleRow}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={showPlan}
              onChange={() => setShowPlan((v) => !v)}
              style={{ margin: 0, accentColor: brandColors.primary }}
            />
            <span>Show plan before drafting</span>
            <span className={styles.planRecommended}>recommended</span>
          </label>
          <span className={styles.planRowSpacer} />
          {showPlan && canGo && (
            <button
              type="button"
              className={styles.skipPlanLink}
              onClick={handleSkipPlan}
              title="Skip the plan and go straight to drafting."
            >
              skip plan
            </button>
          )}
        </div>
        */}
        <div className={styles.footerButtons}>
          <button type="button" className={styles.cancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={mergeClasses(
              styles.primaryButton,
              canGo ? styles.primaryButtonEnabled : styles.primaryButtonDisabled,
            )}
            onClick={handleStart}
            disabled={!canGo}
          >
            {formatPrimaryLabel(selectedCount, showPlan, strategy)}
            <span className={styles.primaryKeyHint}>↵</span>
          </button>
        </div>
      </div>

      {/* Overwrite confirmation. Drafted sections are pickable, but re-drafting
          replaces reviewed content, so a run that would do it stops here first.
          Cancel returns to the picker with the selection intact; Draft anyway
          commits it. */}
      {confirmOverwrite && (
        <div
          className={styles.confirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Re-draft already-drafted sections?"
        >
          <div className={styles.confirmCard}>
            <div className={styles.confirmTitle}>Re-draft already-drafted sections?</div>
            <div className={styles.confirmBody}>
              {selectedDrafted.length === 1
                ? "1 section you picked is already drafted. Drafting will overwrite it — its current content is replaced."
                : `${selectedDrafted.length} sections you picked are already drafted. Drafting will overwrite them — their current content is replaced.`}
            </div>
            <div className={styles.confirmButtons}>
              <button type="button" className={styles.cancelButton} onClick={() => setConfirmOverwrite(false)}>
                Back to sections
              </button>
              <button
                type="button"
                className={mergeClasses(styles.primaryButton, styles.primaryButtonEnabled)}
                style={{ flex: "0 0 auto" }}
                onClick={handleConfirmOverwrite}
              >
                Draft anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoomSelection;

// ---------------------------------------------------------------------------
// PickerRow — extracted as a named component to keep the main
// render tree shallow and to allow React to bail out of row re-
// renders when the row's own inputs don't change. Module-private
// because it's tightly coupled to LoomSelection's styles + palette.
// ---------------------------------------------------------------------------

interface PickerRowProps {
  node: SelectionNode;
  checked: boolean;
  implied: boolean;
  locked: boolean;
  /** Already drafted but still selectable — ticking re-drafts (overwrites) it. */
  drafted?: boolean;
  /** Grouping header (owns no instruction). Renders a derived tri-state
   *  checkbox that cascades to its instructed descendants. */
  isHeader?: boolean;
  /** Header: every instructed descendant is checked. */
  headerAll?: boolean;
  /** Header: some (not all) instructed descendants are checked/implied. */
  headerSome?: boolean;
  gateColor: string;
  onToggle: (id: string) => void;
}

const PickerRow: React.FC<PickerRowProps> = React.memo(
  ({ node, checked, implied, locked, drafted = false, isHeader = false, headerAll = false, headerSome = false, gateColor, onToggle }) => {
    const styles = useStyles();
    // Headers derive an all/some/none tri-state from their descendants;
    // draftable rows use their own checked/implied/locked state.
    const state: "checked" | "some" | "implied" | "locked" | "unchecked" = isHeader
      ? headerAll
        ? "checked"
        : headerSome
          ? "some"
          : "unchecked"
      : locked
        ? "locked"
        : checked
          ? "checked"
          : implied
            ? "implied"
            : "unchecked";

    // Header: clicking always cascades to its subsections. Draftable
    // rows: implied rows aren't directly togglable — writer must untick
    // the section pulling them in (design prototype's cursor:'help'
    // + click guard).
    const handleClick = (): void => {
      if (locked) return;
      if (isHeader) {
        onToggle(node.n);
        return;
      }
      if (!implied) onToggle(node.n);
    };

    const rowNumberColor = state === "unchecked" ? inkColors.ink4 : inkColors.ink2;
    const rowNumberWeight = state === "unchecked" ? 400 : 600;
    const rowTitleColor = state === "unchecked" ? inkColors.ink2 : inkColors.ink;
    // Headers read as group labels — always emphasised.
    const rowTitleWeight = isHeader ? 700 : state === "checked" ? 700 : state === "implied" ? 500 : 400;

  return (
    <label
      className={styles.row}
      style={{
        paddingLeft: `${16 + (node.indent || 0) * 14}px`,
        cursor: locked ? "not-allowed" : !isHeader && implied ? "help" : "pointer",
        opacity: locked ? 0.55 : 1,
      }}
      title={
        isHeader
          ? "Select every subsection below this heading"
          : locked
            ? "Already drafted"
            : drafted
              ? "Already drafted — ticking re-drafts and overwrites it"
              : implied
                ? "Auto-included as a dependency"
                : undefined
      }
    >
      <input
        type="checkbox"
        checked={isHeader ? headerAll || headerSome : checked || implied || locked}
        disabled={isHeader ? false : implied || locked}
        onChange={handleClick}
        style={{
          position: "absolute",
          opacity: 0,
          width: 0,
          height: 0,
          margin: 0,
        }}
      />
      <span className={styles.rowNumber} style={{ color: rowNumberColor, fontWeight: rowNumberWeight }}>
        {node.n}
      </span>
      <span className={styles.rowCheckbox}>
        <span
          className={styles.rowCheckboxBox}
          style={{
            border: state === "unchecked" ? `1.5px solid ${surfaceColors.lineStrong}` : `1.5px solid ${gateColor}`,
            background:
              state === "checked"
                ? gateColor
                : state === "some"
                  ? `${gateColor}55`
                  : state === "implied"
                    ? `${gateColor}33`
                    : state === "locked"
                      ? `${surfaceColors.lineStrong}`
                      : "#fff",
          }}
        >
          {state === "checked" && <Checkmark />}
          {state === "locked" && <LockIcon color={inkColors.ink3} />}
          {state === "some" && (
            <span
              style={{
                width: "6px",
                height: "1.5px",
                background: "#fff",
              }}
            />
          )}
          {state === "implied" && (
            <span
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "1px",
                background: gateColor,
              }}
            />
          )}
        </span>
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle} style={{ color: rowTitleColor, fontWeight: rowTitleWeight }}>
          {node.t}
        </span>
        {implied && !isHeader && (
          <span
            className={styles.depChip}
            style={{
              color: gateColor,
              background: `${gateColor}1A`,
            }}
          >
            dep
          </span>
        )}
        {(locked || drafted) && (
          <span
            className={styles.depChip}
            style={{
              color: inkColors.ink3,
              background: `${surfaceColors.lineStrong}`,
            }}
          >
            drafted
          </span>
        )}
      </span>
      {/* Headers aren't drafted — no per-row time estimate. */}
      {!isHeader && (
        <span
          className={styles.rowEst}
          style={{
            color: state === "unchecked" ? inkColors.ink5 : inkColors.ink4,
          }}
        >
          {node.size === "l" ? "~8m" : node.size === "m" ? "~3m" : "~1m"}
        </span>
      )}
    </label>
  );
  },
); // React.memo(PickerRow)
PickerRow.displayName = "PickerRow";

// Sideways nod to reserved colour tokens so tree-shakers don't strip
// an unused import when the gate palette rotates. The picker already
// uses statusColors indirectly via gateColorAt; keeping the import
// honest costs nothing.
void statusColors;
